import { Controller } from "@hotwired/stimulus"

/**
 * 5-step wizard for planning GO4SPILOT inspections.
 *
 * Steps:
 *   1. Visit type    (card selection)
 *   2. School        (search + card selection)
 *   3. Inspector     (card selection + accompanists)
 *   4. Date & objectives (form fields)
 *   5. Confirmation  (summary)
 */
export default class extends Controller {
  static targets = [
    "step",
    "sidebar",
    "progress",
    "progressLabel",
    "footer",
    "prevBtn",
    "submitBtn",
    "nextBtn",
    "visitTypeInput",
    "schoolIdInput",
    "inspectorIdInput",
    "schoolSearch",
    "schoolCard",
    "inspectorCard",
    "dateInput",
    "durationInput",
    "objectivesInput",
    "summaryType",
    "summarySchool",
    "summaryInspector",
    "summaryDate",
    "summaryDuration",
    "summaryObjectives",
  ]

  static values = {
    current: { type: Number, default: 1 },
    total: { type: Number, default: 5 },
  }

  connect() {
    this.selections = {
      visitType: null,
      schoolId: null,
      inspectorId: null,
    }

    this._render()
  }

  // ──── Navigation ────
  next() {
    if (!this._validateStep(this.currentValue)) return
    if (this.currentValue < this.totalValue) {
      this.currentValue++
      if (this.currentValue === this.totalValue) {
        this._updateSummary()
      }
      this._render()
    }
  }

  prev() {
    if (this.currentValue > 1) {
      this.currentValue--
      this._render()
    }
  }

  goToStep(event) {
    const target = parseInt(event.currentTarget.dataset.step, 10)
    if (target < this.currentValue) {
      this.currentValue = target
      this._render()
    }
  }

  // ──── Card Selection ────
  selectCard(event) {
    const card = event.currentTarget
    const group = card.dataset.group
    const value = card.dataset.value

    // Deselect siblings
    const container = card.closest("[data-card-group]")
    if (container) {
      const selectedClass =
        group === "school"
          ? "g4sp-wf-school-card--selected"
          : group === "inspector"
            ? "g4sp-wf-inspector-card--selected"
            : "g4sp-wf-card--selected"

      container.querySelectorAll("[data-group]").forEach((c) => {
        c.classList.remove(selectedClass)
        const check = c.querySelector(".g4sp-wf-card-check")
        if (check) check.style.display = "none"
      })

      card.classList.add(selectedClass)
      const check = card.querySelector(".g4sp-wf-card-check")
      if (check) check.style.display = ""
    }

    if (group === "visitType") {
      this.selections.visitType = value
      if (this.hasVisitTypeInputTarget) {
        this.visitTypeInputTarget.value = value
      }
    } else if (group === "school") {
      this.selections.schoolId = value
      if (this.hasSchoolIdInputTarget) {
        this.schoolIdInputTarget.value = value
      }
    } else if (group === "inspector") {
      this.selections.inspectorId = value
      if (this.hasInspectorIdInputTarget) {
        this.inspectorIdInputTarget.value = value
      }
    }
  }

  // ──── School Search ────
  searchSchools() {
    if (!this.hasSchoolSearchTarget) return
    const query = this.schoolSearchTarget.value
      .toLowerCase()
      .trim()

    this.schoolCardTargets.forEach((card) => {
      const name = card.dataset.schoolName || ""
      card.style.display =
        !query || name.includes(query) ? "" : "none"
    })
  }

  // ──── Validation ────
  _validateStep(step) {
    switch (step) {
      case 1:
        if (!this.selections.visitType) {
          this._shake(
            this.element.querySelector(
              '[data-card-group="visitType"]'
            )
          )
          return false
        }
        return true
      case 2:
        if (!this.selections.schoolId) {
          this._shake(
            this.element.querySelector(
              '[data-card-group="school"]'
            )
          )
          return false
        }
        return true
      case 3:
        if (!this.selections.inspectorId) {
          this._shake(
            this.element.querySelector(
              '[data-card-group="inspector"]'
            )
          )
          return false
        }
        return true
      case 4: {
        const date = this.hasDateInputTarget
          ? this.dateInputTarget.value
          : ""
        if (!date) {
          this._shake(
            this.dateInputTarget?.closest("div")
          )
          return false
        }
        return true
      }
      case 5:
        return true
      default:
        return true
    }
  }

  disconnect() {
    if (this._shakeTimeout) {
      clearTimeout(this._shakeTimeout)
      this._shakeTimeout = null
    }
  }

  _shake(el) {
    if (!el) return
    el.classList.add("g4sp-wf-shake")
    this._shakeTimeout = setTimeout(
      () => el.classList.remove("g4sp-wf-shake"),
      600
    )
  }

  // ──── Summary ────
  _updateSummary() {
    // Visit type label
    if (this.hasSummaryTypeTarget) {
      const sel = this.element.querySelector(
        '[data-card-group="visitType"]' +
          " .g4sp-wf-card--selected [data-label]"
      )
      this.summaryTypeTarget.textContent =
        sel?.textContent || "—"
    }

    // School name
    if (this.hasSummarySchoolTarget) {
      const schoolSel = this.element.querySelector(
        '[data-card-group="school"]' +
          " .g4sp-wf-school-card--selected [data-label]"
      )
      this.summarySchoolTarget.textContent =
        schoolSel?.textContent || "—"
    }

    // Inspector name
    if (this.hasSummaryInspectorTarget) {
      const inspSel = this.element.querySelector(
        '[data-card-group="inspector"]' +
          " .g4sp-wf-inspector-card--selected" +
          " [data-label]"
      )
      this.summaryInspectorTarget.textContent =
        inspSel?.textContent || "—"
    }

    // Date
    if (
      this.hasSummaryDateTarget &&
      this.hasDateInputTarget
    ) {
      this.summaryDateTarget.textContent =
        this.dateInputTarget.value || "—"
    }

    // Duration
    if (
      this.hasSummaryDurationTarget &&
      this.hasDurationInputTarget
    ) {
      const sel = this.durationInputTarget
      const text =
        sel.selectedIndex > 0
          ? sel.options[sel.selectedIndex].text
          : "—"
      this.summaryDurationTarget.textContent = text
    }

    // Objectives
    if (
      this.hasSummaryObjectivesTarget &&
      this.hasObjectivesInputTarget
    ) {
      const val = this.objectivesInputTarget.value.trim()
      this.summaryObjectivesTarget.textContent =
        val || "—"
    }
  }

  // ──── Render ────
  _render() {
    const step = this.currentValue
    const pct = Math.round(
      (step / this.totalValue) * 100
    )

    // Show/hide step panels
    this.stepTargets.forEach((panel) => {
      const s = parseInt(panel.dataset.step, 10)
      panel.style.display = s === step ? "" : "none"
    })

    // Update sidebar
    this.sidebarTargets.forEach((item) => {
      const s = parseInt(item.dataset.step, 10)
      item.classList.remove(
        "g4sp-wf-step--active",
        "g4sp-wf-step--done",
        "g4sp-wf-step--disabled"
      )
      if (s === step) {
        item.classList.add("g4sp-wf-step--active")
      } else if (s < step) {
        item.classList.add("g4sp-wf-step--done")
      } else {
        item.classList.add("g4sp-wf-step--disabled")
      }
    })

    // Progress bar
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = pct + "%"
    }
    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent =
        `${step} / ${this.totalValue} — ${pct} %`
    }

    // Footer breadcrumb
    if (this.hasFooterTarget) {
      const active = this.sidebarTargets.find(
        (i) =>
          parseInt(i.dataset.step, 10) === step
      )
      if (active) {
        const title =
          active.querySelector("[data-step-title]")
            ?.textContent || ""
        const sub =
          active.querySelector("[data-step-sub]")
            ?.textContent || ""
        this.footerTarget.textContent = ''
        const strong = document.createElement('strong')
        strong.textContent = title
        this.footerTarget.appendChild(strong)
        const span = document.createElement('span')
        span.textContent = ` — ${sub}`
        this.footerTarget.appendChild(span)
      }
    }

    // Prev/Next/Submit visibility
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.style.display =
        step > 1 ? "" : "none"
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.style.display =
        step < this.totalValue ? "" : "none"
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.style.display =
        step === this.totalValue ? "" : "none"
    }
  }
}
